import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";
import { motion, useInView } from "framer-motion";
import {
    FaHandshake,
    FaHospital,
    FaIndustry,
    FaStore,
    FaUserGraduate,
} from "react-icons/fa";
import { BsBuildingsFill } from "react-icons/bs";
import bpsLogo from "../../assets/partner-logos/bps.png";
import cogentLogo from "../../assets/partner-logos/cogent.png";
import geesLogo from "../../assets/partner-logos/gees.jpg";
import laafreshLogo from "../../assets/partner-logos/laafresh.png";
import nuroraLogo from "../../assets/partner-logos/nurora.png";
import sanlitesLogo from "../../assets/partner-logos/sanlites.png";
import sofanovaLogo from "../../assets/partner-logos/sofanova.jpeg";
import stepinLogo from "../../assets/partner-logos/stepin.png";
import tekesLogo from "../../assets/partner-logos/tekes.jpeg";
import thaaiLogo from "../../assets/partner-logos/thaai.png";
import thyLogo from "../../assets/partner-logos/thy.png";
import transunLogo from "../../assets/partner-logos/transun.png";
import veiligheidLogo from "../../assets/partner-logos/veiligheid.jpeg";
import { HeroSection } from "./HeroSection";
import { ProductShowcaseCarousel } from "./ProductShowcaseCarousel";
import { GrowthProcessSection } from "./GrowthProcessSection";
import { SolutionsGrid } from "./SolutionsGrid";
import { WhyChooseHonestiSection } from "./WhyChooseHonestiSection";
import { CrmFaqSection } from "../crm/CrmFaqSection";
import { homeFaqItems } from "../../data/homeContent";
import "../../styles/home-premium.css";
import "../../styles/crm-product.css";

const industries = [
    { name: "Real Estate", icon: <BsBuildingsFill /> },
    { name: "Education", icon: <FaUserGraduate /> },
    { name: "Healthcare", icon: <FaHospital /> },
    { name: "Retail", icon: <FaStore /> },
    { name: "Manufacturing", icon: <FaIndustry /> },
    { name: "Consulting", icon: <FaHandshake /> },
];

const clientPartnerLogos = [
    { name: "BPS", src: bpsLogo },
    { name: "Cogent", src: cogentLogo },
    { name: "Gees", src: geesLogo },
    { name: "Laafresh", src: laafreshLogo },
    { name: "Nurora", src: nuroraLogo },
    { name: "Sanlites", src: sanlitesLogo },
    { name: "Sofanova", src: sofanovaLogo },
    { name: "Stepin", src: stepinLogo },
    { name: "Tekes", src: tekesLogo },
    { name: "Thaai", src: thaaiLogo },
    { name: "THY", src: thyLogo },
    { name: "Transun", src: transunLogo },
    { name: "Veiligheid", src: veiligheidLogo },
];

const metricItems = [
    { label: "Businesses Empowered", value: 100, suffix: "+" },
    { label: "Client Satisfaction", value: 100, suffix: "%" },
    { label: "Faster Operations", value: 70, suffix: "%" },
];

const CountUpStat = ({ value, suffix, label }: { value: number; suffix: string; label: string }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const inView = useInView(ref, { once: false, margin: "-80px" });
    const [display, setDisplay] = useState(0);

    useEffect(() => {
        if (!inView) {
            return;
        }
        const duration = 1300;
        const steps = 50;
        const increment = value / steps;
        let current = 0;
        const timer = window.setInterval(() => {
            current += increment;
            if (current >= value) {
                setDisplay(value);
                window.clearInterval(timer);
                return;
            }
            setDisplay(Math.floor(current));
        }, Math.max(16, Math.floor(duration / steps)));

        return () => window.clearInterval(timer);
    }, [inView, value]);

    return (
        <motion.div
            ref={ref}
            className="premium-metric-tile reveal-up"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.4 }}
            transition={{ duration: 0.5 }}
        >
            <div className="premium-metric-value">
                {display.toLocaleString()}
                {suffix}
            </div>
            <div className="premium-metric-label">{label}</div>
        </motion.div>
    );
};

export const HomePremiumSections = () => {
    const navigate = useNavigate();
    const marqueeLogos = useMemo(
        () => [...clientPartnerLogos, ...clientPartnerLogos],
        []
    );
    const industriesMarquee = useMemo(() => [...industries, ...industries], []);

    return (
        <div className="premium-home">
            <HeroSection />

            <section id="product-ecosystem" className="premium-section premium-section-ecosystem">
                <div className="premium-section-inner">
                    <div className="premium-title-wrap premium-title-wrap--software">
                        <h3>Business Software Solutions</h3>
                        <p className="ecosystem-subheading">
                            Explore purpose-built software platforms that help businesses automate operations, engage
                            customers, improve communication, and accelerate growth.
                        </p>
                    </div>
                    <ProductShowcaseCarousel />
                </div>
            </section>

            <GrowthProcessSection />

            <section className="premium-section premium-section-industries">
                <div className="premium-section-inner">
                    <div className="premium-title-wrap premium-title-wrap--industries">
                        <h3>Industries We Power</h3>
                    </div>

                    <div className="industry-marquee" aria-label="Industries We Power">
                        <div className="industry-marquee__fade industry-marquee__fade--left" aria-hidden="true" />
                        <div className="industry-marquee__fade industry-marquee__fade--right" aria-hidden="true" />
                        <div className="industry-marquee__viewport">
                            <div className="industry-tiles">
                                {industriesMarquee.map((industry, index) => (
                                    <div key={`${industry.name}-${index}`} className="industry-tile">
                                        <span className="industry-tile__icon">{industry.icon}</span>
                                        <span className="industry-tile__name">{industry.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="premium-section premium-section-solutions">
                <div className="premium-section-inner">
                    <div className="premium-title-wrap premium-title-wrap--solutions">
                        <h3>Digital Solutions</h3>
                        <p className="ecosystem-subheading">
                            Explore the services that help businesses build, market, automate, and scale faster.
                        </p>
                    </div>
                    <SolutionsGrid />
                </div>
            </section>

            <WhyChooseHonestiSection />

            <section id="client-trust" className="premium-section premium-section-trust">
                <div className="premium-section-inner">
                    <div className="premium-title-wrap premium-title-wrap--trust">
                        <h3>Trusted by Growth-Focused Clients</h3>
                    </div>
                    <div className="client-logo-marquee" aria-label="Partner logos">
                        <div className="client-logo-marquee__fade client-logo-marquee__fade--left" aria-hidden="true" />
                        <div className="client-logo-marquee__fade client-logo-marquee__fade--right" aria-hidden="true" />
                        <div className="client-logo-marquee__viewport">
                            <div className="client-logo-marquee__track">
                                {marqueeLogos.map((logo, index) => (
                                    <div key={`${logo.name}-${index}`} className="client-logo-marquee__item">
                                        <img
                                            src={logo.src}
                                            alt={`${logo.name} logo`}
                                            className="client-logo-marquee__logo"
                                            loading="lazy"
                                            draggable={false}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="business-impact" className="premium-section premium-section-metrics">
                <div className="premium-section-inner">
                    <div className="premium-title-wrap">
                        <h3>Measured Performance Across Every Engagement</h3>
                    </div>
                    <div className="premium-metrics-grid">
                        {metricItems.map((item) => (
                            <CountUpStat key={item.label} {...item} />
                        ))}
                    </div>
                </div>
            </section>

            <CrmFaqSection
                title="Frequently Asked Questions"
                lead="Everything you need to know about Honesti Digital's software platforms, business solutions, and implementation services."
                items={homeFaqItems}
                showEyebrow={false}
            />

            <section className="premium-section final-cta">
                <div className="premium-section-inner final-cta-shell">
                    <h3>Launch Your Next Software Advantage with Honesti Digital</h3>
                    <p>Discover product-led growth systems, intelligent automation, and transformation-ready execution.</p>
                    <div className="hero-actions">
                        <Button type="primary" className="premium-btn-primary" onClick={() => navigate("/software")}>
                            Explore Product Ecosystem
                        </Button>
                        <Button className="premium-btn-secondary" onClick={() => navigate("/contact")}>
                            Start a Conversation
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
};
